/** The record editor's session as an outline needs it, without its cost (the tour plan
 *  §5): the editor's context changes on every draft change, and a component that reads it
 *  re-renders with it. The bridge reads it once and hands the outline the parts that do
 *  not change per keystroke — value formatting, the focus registry, invalid inputs — so the
 *  rows and inputs subscribe only to their own records. */
import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { RecordKey } from '@/features/records/draft/changeSet';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import type { RecordField } from '@/features/records/editor/types';

export interface OutlineEditor {
  formatValue: (field: RecordField, rec: RecordKey, value: unknown) => string;
  /** The catalog field of a path; `rec` tells a key two entities share apart. */
  fieldOf: (path: string, rec?: RecordKey) => RecordField | undefined;
  registerFocus: (rec: RecordKey, path: string, focus: () => void) => () => void;
  focusField: (rec: RecordKey, path: string) => void;
  setInvalidInput: (rec: RecordKey, path: string, message: string | null) => void;
}

const Ctx = createContext<OutlineEditor | null>(null);

export function OutlineEditorBridge({ children }: { children: ReactNode }) {
  const editor = useRecordEditor();
  const { formatValue, fieldForPath, registerFocus, focusField, setInvalidInput } = editor;
  const value = useMemo<OutlineEditor>(
    () => ({ formatValue, fieldOf: fieldForPath, registerFocus, focusField, setInvalidInput }),
    [formatValue, fieldForPath, registerFocus, focusField, setInvalidInput],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOutlineEditor(): OutlineEditor {
  const v = useContext(Ctx);
  if (!v) throw new Error('useOutlineEditor must be used inside <OutlineEditorBridge>');
  return v;
}

/** A ledger row asks to focus an outline field whose input is not mounted (another row is
 *  open): the page hands the request to the outline's owner, which opens the row and
 *  focuses the field once it renders. One per page. */
export class OutlineFocusChannel {
  private listener: ((rec: RecordKey, path: string) => void) | null = null;

  listen(fn: (rec: RecordKey, path: string) => void): () => void {
    this.listener = fn;
    return () => {
      if (this.listener === fn) this.listener = null;
    };
  }

  /** True when an outline took the request. */
  request(rec: RecordKey, path: string): boolean {
    if (!this.listener) return false;
    this.listener(rec, path);
    return true;
  }
}
